import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/common";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Shield, Clock } from "lucide-react";
import { toast } from "sonner";

const asList = (d) => (Array.isArray(d) ? d : d?.items || d?.logs || d?.results || []);

export default function BraceLogs() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [prescribed, setPrescribed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [br, pres] = await Promise.allSettled([api.braceLogs(user.id), api.getBracePrescription(user.id)]);
      if (br.status === "fulfilled") { setLogs(asList(br.value?.logs || br.value)); setSummary(br.value?.summary || null); }
      else setError(errMsg(br.reason));
      if (pres.status === "fulfilled") setPrescribed(pres.value?.prescribed_hours ?? null);
    } catch (e) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const h = parseFloat(hours);
    if (isNaN(h) || h < 0) { toast.error("Inserisci un numero di ore valido"); return; }
    setSaving(true);
    try {
      await api.createBraceLog({ date, hours: h });
      toast.success("Utilizzo registrato");
      setHours(""); setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader
        title="Utilizzo busto"
        subtitle={prescribed != null ? `Obiettivo prescritto: ${prescribed} ore al giorno.` : "Registra le ore di utilizzo del busto ortopedico."}
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-brace-button"><Plus className="mr-2 h-4 w-4" /> Registra</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Registra utilizzo busto</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Data</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" data-testid="brace-date-input" /></div>
                <div>
                  <Label>Ore indossate</Label>
                  <div className="mt-1.5 flex items-center gap-2">
                    <Input type="number" min="0" max="24" step="0.5" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="es. 8" className="w-28" data-testid="brace-hours-input" />
                    <span className="text-base font-semibold text-muted-foreground" data-testid="brace-prescribed-denominator">/ {prescribed != null ? prescribed : "—"} ore prescritte</span>
                  </div>
                  {prescribed == null && <p className="mt-1 text-xs text-muted-foreground">Le ore prescritte non sono ancora state impostate dal tuo fisioterapista.</p>}
                </div>
              </div>
              <DialogFooter><Button onClick={save} disabled={saving} data-testid="save-brace-button">{saving ? "Salvataggio..." : "Salva"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {!loading && !error && (summary || prescribed != null) && (
        <div className="mb-6 grid gap-3 sm:grid-cols-3" data-testid="brace-summary">
          <div className="rounded-2xl bg-[#e6f0e9] p-5">
            <p className="text-3xl font-bold text-[#1f3a5f]">{summary?.avg_hours != null ? `${summary.avg_hours}h` : "—"}</p>
            <p className="text-sm text-[#1f3a5f]/70">Media indossate/die</p>
          </div>
          <div className="rounded-2xl bg-[#fbe9db] p-5">
            <p className="text-3xl font-bold text-[#1f3a5f]">{prescribed != null ? `${prescribed}h` : "—"}</p>
            <p className="text-sm text-[#1f3a5f]/70">Ore prescritte/die</p>
          </div>
          <div className="rounded-2xl bg-[#e6f0e9] p-5">
            <p className="text-3xl font-bold text-[#1f3a5f]">{prescribed && summary?.avg_hours != null ? `${Math.round((summary.avg_hours / prescribed) * 100)}%` : "—"}</p>
            <p className="text-sm text-[#1f3a5f]/70">Aderenza</p>
          </div>
        </div>
      )}

      {loading ? <Loading /> : error ? <ErrorState message={error} onRetry={load} /> :
        logs.length === 0 ? <EmptyState title="Nessuna registrazione" subtitle="Registra il primo utilizzo del busto." /> : (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="brace-list">
            {logs.map((it, i) => (
              <Card key={it.id || it._id || i} className="border-border/70">
                <CardContent className="flex items-center gap-4 p-4">
                  <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary"><Shield className="h-5 w-5" /></div>
                  <div>
                    <p className="text-lg font-bold">{it.hours ?? it.hours_worn ?? "—"}<span className="text-sm font-normal text-muted-foreground">{prescribed != null ? `/${prescribed}` : ""} ore</span></p>
                    <p className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" />
                      {new Date(it.date || it.created_at || Date.now()).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}
